import * as React from 'react';
import { FC, useCallback } from 'react';

import { NumberInput } from 'components/ucl/number-input';
import { useRedeemRewardState } from 'pages/cart/cart-item/hooks/use-redeem-reward-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useRewardRedemptionLimit } from 'state/loyalty/hooks/use-reward-redemption-limit';
import {
  ICartEntryType,
  MAX_IN_RESTAURANT_CART_QUANTITY,
  MIN_IN_RESTAURANT_CART_QUANTITY,
  useInRestaurantRedemptionContext,
} from 'state/loyalty/in-restaurant-redemption';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';

import { displayValueStyleProps, incrementorStyleProps } from './styled';
import { IInRestaurantIncrementorProps } from './types';

const RewardRedemptionIncrementor: FC<React.PropsWithChildren<IInRestaurantIncrementorProps>> = ({
  cartEntry,
  allowRemove = false,
}) => {
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const rewardRedemptionLimit = useRewardRedemptionLimit();
  const {
    inRestaurantRedemptionCart,
    updateInRestaurantRedemptionEntryQuantity,
    removeInRestaurantRedemptionEntry,
  } = useInRestaurantRedemptionContext();

  const { quantity, details } = cartEntry;

  const otherRewardQuantity = (inRestaurantRedemptionCart ?? [])
    .filter(entry => entry.type === ICartEntryType.REWARD && entry !== cartEntry)
    .reduce((sum, entry) => sum + (entry.quantity ?? 0), 0);

  const maxQuantity = enableRewardUnstacking
    ? Math.max(1, rewardRedemptionLimit - otherRewardQuantity)
    : MAX_IN_RESTAURANT_CART_QUANTITY;

  const {
    incentiveNotInMenu,
    limitPerOrderMet,
    hasSufficientBalance,
    redeemRewardLoading,
  } = useRedeemRewardState({
    reward: details.engineReward,
    quantity,
    cartId: details.engineReward.id,
  });

  const handleChange = useCallback(
    (value: number) => {
      // `updateInRestaurantRedemptionEntryQuantity` silently rejects values below the
      // cart's minimum quantity, so a decrement from 1 would otherwise be a no-op.
      // When opt-in via `allowRemove`, treat hitting 0 as a removal of the entry.
      if (allowRemove && value < MIN_IN_RESTAURANT_CART_QUANTITY) {
        removeInRestaurantRedemptionEntry(cartEntry);
      } else {
        updateInRestaurantRedemptionEntryQuantity(cartEntry, value);
      }
      hapticNotification({ type: HapticsNotificationType.SUCCESS });
    },
    [
      allowRemove,
      cartEntry,
      removeInRestaurantRedemptionEntry,
      updateInRestaurantRedemptionEntryQuantity,
    ]
  );

  // All hooks must be called before this early return to satisfy React rules.
  if (enableRewardUnstacking && rewardRedemptionLimit <= 1) {
    return null;
  }

  const disableIncrement =
    incentiveNotInMenu || limitPerOrderMet || !hasSufficientBalance || quantity >= maxQuantity;

  const minQuantity = allowRemove ? 0 : MIN_IN_RESTAURANT_CART_QUANTITY;

  return (
    <NumberInput
      ariaLabel={`${quantity}`}
      min={minQuantity}
      max={maxQuantity}
      value={quantity}
      onChange={handleChange}
      disableIncrement={disableIncrement}
      disabled={redeemRewardLoading}
      decrementAriaLabel={`Decrement ${quantity} quantity`}
      incrementAriaLabel={`Increment ${quantity} quantity`}
      displayValueStyleProps={displayValueStyleProps}
      incrementorStyleProps={incrementorStyleProps}
      hideNumberInput
      displayedValue={quantity}
    />
  );
};

export default RewardRedemptionIncrementor;
